import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ALDashboard.css';
import { getToken } from '../../../../../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ALDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalValue: 0,
    totalCount: 0,
    totalCountFiltered: 0,
    oldestPrioridadeDate: null,
    formattedOldestPrioridadeDate: 'N/A',
    breakdown: {
      prioridade: { value: 0, count: 0 },
      regular: { value: 0, count: 0 }
    },
    filteredBreakdown: {
      prioridade: { count: 0 },
      regular: { count: 0 }
    },
    rclTotals: {
      totalGeral: 0,
      totalPrioritarios: 0,
      totalRegulares: 0,
      counts: {
        prioritarios: 0,
        regulares: 0
      }
    }
  });
  const [annualRCLValue, setAnnualRCLValue] = useState('');
  const [contaRCL109Value, setContaRCL109Value] = useState('');
  const [projection, setProjection] = useState(null);
  const [loadingProjection, setLoadingProjection] = useState(false);
  const [activeTab, setActiveTab] = useState('statistics');
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [duplicateStats, setDuplicateStats] = useState({ totalPairs: 0, totalRecords: 0 });

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (activeTab === 'duplicates' && duplicates.length === 0) {
      fetchDuplicates();
    }
  }, [activeTab]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorioAL/rcl/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar estatísticas');
      }

      const data = await response.json();
      
      // Format the data
      setStatistics({
        totalValue: data.totalValue || 0,
        totalCount: data.totalCount || 0,
        totalCountFiltered: data.totalCountFiltered || 0,
        oldestPrioridadeDate: data.oldestPrioridadeDate,
        formattedOldestPrioridadeDate: data.oldestPrioridadeDate ? formatDate(data.oldestPrioridadeDate) : 'N/A',
        breakdown: data.breakdown || {
          prioridade: { value: 0, count: 0 },
          regular: { value: 0, count: 0 }
        },
        filteredBreakdown: data.filteredBreakdown || {
          prioridade: { count: 0 },
          regular: { count: 0 }
        },
        rclTotals: {
          totalGeral: data.totalGeral || 0,
          totalPrioritarios: data.totalPrioritarios || 0,
          totalRegulares: data.totalRegulares || 0,
          counts: {
            prioritarios: data.countPrioritarios || 0,
            regulares: data.countRegulares || 0
          }
        }
      });
      
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle different date formats
    if (dateString.includes('/')) {
      // Already in DD/MM/YYYY format
      return dateString;
    }
    
    // Convert from ISO or other formats
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleBackClick = () => {
    navigate('/admin/tabela-rcl');
  };

  const handleCalculateProjection = async () => {
    if (!annualRCLValue || parseFloat(annualRCLValue.replace(/\./g, '').replace(',', '.')) <= 0) {
      alert('Por favor, insira um valor anual RCL válido');
      return;
    }

    try {
      setLoadingProjection(true);
      setError(null);
      
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorioAL/rcl/projection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annualRCLValue: parseFloat(annualRCLValue.replace(/\./g, '').replace(',', '.')),
          contaRCL109: parseFloat(contaRCL109Value.replace(/\./g, '').replace(',', '.')) || 0
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao calcular projeção');
      }

      const data = await response.json();
      setProjection(data);
      
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoadingProjection(false);
    }
  };

  const handleRCLValueChange = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except comma and dot
    value = value.replace(/[^0-9,]/g, '');
    
    // Replace comma with dot for parsing
    const numericValue = value.replace(',', '.');
    
    // Update the state with the numeric value
    if (value === '' || !isNaN(parseFloat(numericValue))) {
      setAnnualRCLValue(value);
    }
  };

  const handleContaRCL109Change = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except comma and dot
    value = value.replace(/[^0-9,]/g, '');
    
    // Replace comma with dot for parsing
    const numericValue = value.replace(',', '.');
    
    // Update the state with the numeric value
    if (value === '' || !isNaN(parseFloat(numericValue))) {
      setContaRCL109Value(value);
    }
  };

  const fetchDuplicates = async () => {
    try {
      setLoadingDuplicates(true);
      
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorioAL/duplicates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar duplicados');
      }

      const data = await response.json();
      setDuplicates(data.duplicates || []);
      setDuplicateStats({
        totalPairs: data.duplicates?.length || 0,
        totalRecords: (data.duplicates?.length || 0) * 2
      });
      
    } catch (err) {
      console.error('Erro ao buscar duplicados:', err);
      setError(err.message);
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Dashboard RCL - Alagoas (AL)', pageWidth / 2, 20, { align: 'center' });
    
    let yPos = 40;
    
    if (activeTab === 'statistics') {
      // Statistics section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Estatísticas RCL', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const summaryData = [
        ['Total Geral:', formatCurrency(statistics.rclTotals.totalGeral)],
        ['Total Prioritários:', formatCurrency(statistics.rclTotals.totalPrioritarios)],
        ['Total Regulares:', formatCurrency(statistics.rclTotals.totalRegulares)],
        ['Qtd Total:', statistics.totalCount.toString()],
        ['Qtd Total - até a data recebimento 31/12/2024:', statistics.totalCountFiltered.toString()],
        ...(projection?.contaRCL109 > 0 ? [['Conta RCL EC-109(7 meses):', formatCurrency((projection.contaRCL109)*(7/12))]] : []),
        ...(projection ? [['Valor Total RCL(2025)(EC-109 + PEC 66)(Valor = Somatória * Cálculo):', formatCurrency(((projection.contaRCL109 * (7/12) || 0) + ((5/12) * projection.annualRCLValue)) * 1.33)]] : [])
      ];
      
      summaryData.forEach((item, index) => {
        // Check if we need a new page before drawing
        if (yPos + 10 > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.text(item[0], 14, yPos);
        doc.text(item[1], 120, yPos);
        yPos += 6;
      });
      
      // Projection table if available
      if (projection && projection.projection) {
        yPos += 10;
        
        if (yPos + 40 > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Projeção de Pagamentos RCL', 14, yPos);
        yPos += 10;
        
        const projectionTableData = projection.projection.map(item => [
          item.year.toString(),
          formatCurrency(item.paidValue),
          formatCurrency(item.remainingBudget),
          formatCurrency(item.totalPaidSoFar),
          item.remainingCount.toString(),
          item.paidCount.toString()
        ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Ano', 'Valor Pago', 'Caixa Restante', 'Total Pago', 'Precatórios Restantes', 'Qtd Precatórios Pgos']],
          body: projectionTableData,
          styles: { fontSize: 8 },
          theme: 'grid'
        });
      }
    } else if (activeTab === 'duplicates') {
      // Duplicates section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Duplicados entre Regular e Prioridade - Alagoas', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total de pares duplicados: ${duplicateStats.totalPairs}`, 14, yPos);
      yPos += 6;
      doc.text(`Total de registros: ${duplicateStats.totalRecords}`, 14, yPos);
      yPos += 15;
      
      if (duplicates.length > 0) {
        const duplicatesTableData = [];
        
        duplicates.forEach((dup, index) => {
          // Regular entry
          duplicatesTableData.push([
            dup.numero_precatorio,
            'Regular',
            formatCurrency(dup.regular.valor_atualizado || 0),
            dup.regular.ente || '',
            dup.regular.natureza || '',
            dup.regular.recebimento || ''
          ]);
          
          // Priority entry  
          duplicatesTableData.push([
            dup.numero_precatorio,
            'Prioridade',
            formatCurrency(dup.prioridade.valor_atualizado || 0),
            dup.prioridade.ente || '',
            dup.prioridade.natureza || '',
            dup.prioridade.recebimento || ''
          ]);
        });
        
        autoTable(doc, {
          startY: yPos,
          head: [['Número Precatório', 'Tipo', 'Valor', 'Ente', 'Natureza', 'Recebimento']],
          body: duplicatesTableData,
          styles: { fontSize: 8 },
          theme: 'grid',
          pageBreak: 'auto'
        });
      }
    }
    
    // Save the PDF
    doc.save(`dashboard-rcl-alagoas-${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`);
  };

  if (loading) {
    return (
      <div className="al-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando dados de Alagoas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="al-dashboard">
        <div className="error-container">
          <h2>Erro</h2>
          <p>{error}</p>
          <button onClick={fetchStatistics} className="retry-button">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="al-dashboard">
      <div className="dashboard-header">
        <button onClick={handleBackClick} className="back-button">
          ← Voltar para Tabela RCL
        </button>
        <h1>Dashboard RCL - Alagoas (AL)</h1>
        <button onClick={exportToPDF} className="export-button">
          📄 Exportar PDF
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          Estatísticas
        </button>
        <button 
          className={`tab-button ${activeTab === 'duplicates' ? 'active' : ''}`}
          onClick={() => setActiveTab('duplicates')}
        >
          Duplicados ({duplicateStats.totalPairs})
        </button>
      </div>

      {activeTab === 'statistics' && (
        <div className="statistics-section">
          {/* RCL Statistics */}
          <div className="statistics-grid">
            <div className="stat-card">
              <h3>Total Geral</h3>
              <p className="stat-value">{formatCurrency(statistics.rclTotals.totalGeral)}</p>
              <span className="stat-label">{statistics.totalCount} precatórios</span>
            </div>
            
            <div className="stat-card priority">
              <h3>Total Prioritários</h3>
              <p className="stat-value">{formatCurrency(statistics.rclTotals.totalPrioritarios)}</p>
              <span className="stat-label">{statistics.rclTotals.counts.prioritarios} precatórios</span>
            </div>
            
            <div className="stat-card regular">
              <h3>Total Regulares</h3>
              <p className="stat-value">{formatCurrency(statistics.rclTotals.totalRegulares)}</p>
              <span className="stat-label">{statistics.rclTotals.counts.regulares} precatórios</span>
            </div>
            
            <div className="stat-card filtered">
              <h3>Total Precatório - até a data recebimento 31/12/2024</h3>
              <p className="stat-value">{statistics.totalCountFiltered}</p>
              <div className="filtered-breakdown">
                <span>Prioritários: {statistics.filteredBreakdown.prioridade.count}</span>
                <span>Regulares: {statistics.filteredBreakdown.regular.count}</span>
              </div>
            </div>
          </div>

          {/* RCL Projection Calculator */}
          <div className="projection-section">
            <h2>Calculadora de Projeção RCL</h2>
            <div className="input-group">
              <div className="input-field">
                <label htmlFor="annualRCL">Valor Anual RCL:</label>
                <input
                  type="text"
                  id="annualRCL"
                  value={annualRCLValue}
                  onChange={handleRCLValueChange}
                  placeholder="Digite o valor anual..."
                />
              </div>
              <div className="input-field">
                <label htmlFor="contaRCL109">Conta RCL EC-109:</label>
                <input
                  type="text"
                  id="contaRCL109"
                  value={contaRCL109Value}
                  onChange={handleContaRCL109Change}
                  placeholder="Digite o valor EC-109..."
                />
              </div>
              <button 
                onClick={handleCalculateProjection}
                disabled={loadingProjection}
                className="calculate-button"
              >
                {loadingProjection ? 'Calculando...' : 'Calcular Projeção'}
              </button>
            </div>

            {projection && (
              <div className="projection-results">
                <h3>Resultados da Projeção</h3>
                <div className="projection-summary">
                  <div className="summary-item">
                    <span>Valor RCL Anual:</span>
                    <span>{formatCurrency(projection.annualRCLValue)}</span>
                  </div>
                  {projection.contaRCL109 > 0 && (
                    <div className="summary-item">
                      <span>Conta RCL EC-109(7 meses):</span>
                      <span>{formatCurrency((projection.contaRCL109)*(7/12))}</span>
                    </div>
                  )}
                  <div className="summary-item">
                    <span>Valor Total RCL(2025)(EC-109 + PEC 66)(Valor = Somatória * Cálculo):</span>
                    <span>{formatCurrency(((projection.contaRCL109 * (7/12) || 0) + ((5/12) * projection.annualRCLValue)) * 1.33)}</span>
                  </div>
                  <div className="summary-item">
                    <span>Total de Precatórios:</span>
                    <span>{projection.totalPrecatorios}</span>
                  </div>
                  <div className="summary-item">
                    <span>Valor Total dos Precatórios:</span>
                    <span>{formatCurrency(projection.totalValue)}</span>
                  </div>
                  <div className="summary-item">
                    <span>Anos para Pagamento Completo:</span>
                    <span>{projection.yearsToPayAll}</span>
                  </div>
                  <div className="summary-item">
                    <span>Último Ano de Pagamento:</span>
                    <span>{projection.lastPaymentYear}</span>
                  </div>
                </div>

                <div className="projection-phases">
                  <div className="phase-item">
                    <h4>Fase 1 (até 2025 + Prioritários)</h4>
                    <p>Quantidade: {projection.phase1Stats.count}</p>
                    <p>Valor: {formatCurrency(projection.phase1Stats.value)}</p>
                  </div>
                  <div className="phase-item">
                    <h4>Fase 2 (Restantes)</h4>
                    <p>Quantidade: {projection.phase2Stats.count}</p>
                    <p>Valor: {formatCurrency(projection.phase2Stats.value)}</p>
                  </div>
                </div>

                {projection.projection && (
                  <div className="projection-table">
                    <h4>Projeção Detalhada por Ano</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Ano</th>
                          <th>Valor Pago</th>
                          <th>Caixa Restante</th>
                          <th>Total Pago</th>
                          <th>Precatórios Restantes</th>
                          <th>Qtd Precatórios Pgos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projection.projection.map((item, index) => (
                          <tr key={index}>
                            <td>{item.year}</td>
                            <td>{formatCurrency(item.paidValue)}</td>
                            <td>{formatCurrency(item.remainingBudget)}</td>
                            <td>{formatCurrency(item.totalPaidSoFar)}</td>
                            <td>{item.remainingCount}</td>
                            <td>{item.paidCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'duplicates' && (
        <div className="duplicates-section">
          <div className="duplicates-header">
            <h2>Duplicados entre Regular e Prioridade - Alagoas</h2>
            <div className="duplicates-stats">
              <span>Total de pares duplicados: <strong>{duplicateStats.totalPairs}</strong></span>
              <span>Total de registros: <strong>{duplicateStats.totalRecords}</strong></span>
            </div>
          </div>

          {loadingDuplicates ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Carregando duplicados...</p>
            </div>
          ) : duplicates.length > 0 ? (
            <div className="duplicates-table">
              <table>
                <thead>
                  <tr>
                    <th>Número Precatório</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Ente</th>
                    <th>Natureza</th>
                    <th>Recebimento</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicates.map((dup, index) => (
                    <React.Fragment key={index}>
                      <tr className="duplicate-regular">
                        <td>{dup.numero_precatorio}</td>
                        <td><span className="type-badge regular">Regular</span></td>
                        <td>{formatCurrency(dup.regular.valor_atualizado || 0)}</td>
                        <td>{dup.regular.ente}</td>
                        <td>{dup.regular.natureza}</td>
                        <td>{dup.regular.recebimento}</td>
                      </tr>
                      <tr className="duplicate-priority">
                        <td>{dup.numero_precatorio}</td>
                        <td><span className="type-badge priority">Prioridade</span></td>
                        <td>{formatCurrency(dup.prioridade.valor_atualizado || 0)}</td>
                        <td>{dup.prioridade.ente}</td>
                        <td>{dup.prioridade.natureza}</td>
                        <td>{dup.prioridade.recebimento}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-duplicates">
              <p>Nenhum duplicado encontrado entre as coleções Regular e Prioridade.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ALDashboard;